import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AppBaseComponent } from '@common/components';
import { environment } from '@environments/environment';
import { AsdaDateSelectDialogComponent, AsdaLogoutPopupComponent } from '@shared/components';
import { DATE_TIME_FORMAT } from '@shared/constants';
import { API_DATE_FORMAT, ConfigKey, ILastReportRunResponse, IProfile } from '@shared/models';
import { ApiService, ConfigService, LastReportRunsService, ProfileService } from '@shared/services';
import { BnNgIdleService } from 'bn-ng-idle';
import dayjs from 'dayjs';
import { isUndefined } from 'lodash-es';
import { catchError, switchMap, takeUntil, throwError } from 'rxjs';
import { AsdaDirectSqlDialogComponent } from '../asda-direct-sql-dialog/asda-direct-sql-dialog.component';
import { AsdaUploadConfigFileDialogComponent } from '../asda-upload-config-file-dialog/asda-upload-config-file-dialog.component';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    imports: [CommonModule, MatButtonModule, MatTooltipModule, MatIconModule, MatSlideToggleModule, MatMenuModule, RouterModule],
})
export class NavbarComponent extends AppBaseComponent implements OnInit {
    @Input() helpUrl: string;

    lastReportRun?: ILastReportRunResponse;
    profile: IProfile;
    copyPlaceholder = 'Copy ASDA Week';
    asdaWeek: string;
    newLastReportRun: boolean;
    newLastReportRunTimeout: ReturnType<typeof setTimeout> | null = null;
    defaultIdleTimeInSeconds = 15 * 60; //15 minutes
    defaultIdleTimeWarningInSeconds = 2 * 60; //2 minutes

    readonly DATE_TIME_FORMAT = DATE_TIME_FORMAT;
    readonly showNewReportRunNotificationTimeout = 5 * 60 * 1000; // 5 minutes

    constructor(
        public configService: ConfigService,
        public profileService: ProfileService,
        private apiService: ApiService,
        private matDialog: MatDialog,
        private authService: MsalService,
        private lastReportRunsService: LastReportRunsService,
        private bnIdle: BnNgIdleService,
    ) {
        super();
        this.lastReportRunsService.lastReportRun$.pipe(takeUntil(this.destroyed$)).subscribe((lastReportRun) => {
            if (this.lastReportRun && this.lastReportRun?.id !== lastReportRun.id) {
                this.showNewLastReportRunNotification();
            }
            this.lastReportRun = lastReportRun;
        });
    }

    ngOnInit(): void {
        if (window.location.pathname !== '/logged-out') {
            if (environment.useAuthorization) {
                this.loadProfile();
                this.startIdleTimeWatch();
            }

            this.loadAsdaWeek();
        }
    }

    onSwitchEndpoint(): void {
        const newEndpoint =
            this.configService.appConfig.apiEndpoint === this.configService.apiEndpoint
                ? this.configService.appConfig.apimEndpoint
                : this.configService.appConfig.apiEndpoint;
        this.configService.setApiEndpoint(newEndpoint);
    }

    onOpenCalendarDialog(): void {
        this.matDialog.open(AsdaDateSelectDialogComponent, {
            width: '550px',
            maxHeight: '800px',
            disableClose: true,
        });
    }

    onOpenDirectSqlDialog(): void {
        this.matDialog.open(AsdaDirectSqlDialogComponent, {
            width: '90vw',
            height: '90vh',
        });
    }

    onOpenUploadConfigDialog(): void {
        this.matDialog.open(AsdaUploadConfigFileDialogComponent, {
            width: '550px',
        });
    }

    onCopyAsdaWeek(text: string): void {
        this.copyPlaceholder = 'Copied!';
        navigator.clipboard
            .writeText(text)
            .then(() => setTimeout(() => (this.copyPlaceholder = 'Copy ASDA Week'), 1000))
            .catch(() => {});
    }

    onLogout(): void {
        this.authService.logout();
    }

    protected loadProfile(): void {
        this.profileService
            .fetchProfile()
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
                takeUntil(this.destroyed$),
            )
            .subscribe((response) => {
                this.profile = response;
                this.apiService.addUserLogin();
            });
    }

    protected loadAsdaWeek(): void {
        this.apiService
            .fetchCalendar({
                date: dayjs().format(API_DATE_FORMAT),
            })
            .pipe(takeUntil(this.destroyed$))
            .subscribe((response) => {
                this.asdaWeek = response[0]?.asdaWeek.toString() ?? '';
            });
    }

    protected startIdleTimeWatch(): void {
        this.apiService
            .fetchConfig()
            .pipe(
                takeUntil(this.destroyed$),
                switchMap((configResponse) => {
                    const idleTimeValue = configResponse.find((config) => config.key === ConfigKey.USER_IDLE_TIME)?.value ?? undefined;
                    const idleTime = isUndefined(idleTimeValue)
                        ? this.defaultIdleTimeInSeconds - this.defaultIdleTimeWarningInSeconds
                        : Number(idleTimeValue) - this.defaultIdleTimeWarningInSeconds;
                    return this.bnIdle.startWatching(Number(idleTime));
                }),
            )
            .subscribe((isTimedOut: boolean) => {
                if (isTimedOut) {
                    this.showWarning();
                }
            });
    }

    private handleLogout(): void {
        const accounts = this.authService.instance.getAllAccounts();
        const activeAccount = this.authService.instance.getActiveAccount();
        const accountToLogout = activeAccount || (accounts.length > 0 ? accounts[0] : null);
        this.authService.logoutRedirect({
            postLogoutRedirectUri: '/logged-out',
            account: accountToLogout,
        });
    }

    private showWarning(): void {
        let logoutTimeout: ReturnType<typeof setTimeout>;
        const warningDialog = this.matDialog.open(AsdaLogoutPopupComponent, {
            data: {
                message: 'Your session is about to expire. Do you want to extend your session?',
            },
        });

        warningDialog.afterOpened().subscribe(() => {
            logoutTimeout = setTimeout(() => {
                warningDialog.close('logout');
            }, this.defaultIdleTimeWarningInSeconds * 1000);
        });

        warningDialog.afterClosed().subscribe((result) => {
            if (result === 'ok') {
                warningDialog.close();
                this.bnIdle.resetTimer();
                clearTimeout(logoutTimeout);
            } else {
                warningDialog.close();
                this.handleLogout();
            }
        });
    }

    protected showNewLastReportRunNotification(): void {
        this.newLastReportRun = true;
        if (this.newLastReportRunTimeout) {
            clearTimeout(this.newLastReportRunTimeout);
        }
        this.newLastReportRunTimeout = setTimeout(() => (this.newLastReportRun = false), this.showNewReportRunNotificationTimeout);
    }
}
